import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { paymentMethodLabel } from '../utils/academyPaymentMethods';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtMoney } from './AcademyShared';

export default function AcademyPayments() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ last_page: 1, total: 0 });
  const [error, setError] = useState('');
  const [overdueError, setOverdueError] = useState('');
  const listRequest = useRef(0);
  const overdueRequest = useRef(0);
  const [recording, setRecording] = useState(false);

  const waiveInstallment = async (installment) => {
    const reason = window.prompt('Reason for waiving this payment:');
    if (reason === null || !reason.trim()) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/installments/${installment.id}/waive`, { method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Unable to waive payment'); return; }
    refreshPayments();
  };
  const [showOverdue, setShowOverdue] = useState(false);

  const fetchList = useCallback(async () => {
    const request = ++listRequest.current;
    setLoading(true);
    setError('');
    try {
      let url = `${API}/companies/${companyId}/academy/payments?per_page=50&page=${page}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Unable to load payments.');
      if (request !== listRequest.current) return;
      setList(Array.isArray(json.data) ? json.data : []);
      setPagination({ last_page: Math.max(1, Number(json.last_page) || 1), total: Number(json.total) || 0 });
      if (json.last_page && page > json.last_page) setPage(json.last_page);
    } catch (e) {
      if (request === listRequest.current) { setError(e.message || 'Unable to load payments.'); setList([]); }
    } finally { if (request === listRequest.current) setLoading(false); }
  }, [companyId, search, page]);

  const fetchOverdue = useCallback(async () => {
    const request = ++overdueRequest.current;
    setOverdueError('');
    try {
      const rows = [];
      let duePage = 1, lastPage = 1;
      do {
        const response = await fetch(`${API}/companies/${companyId}/academy/installments/overdue?per_page=100&page=${duePage}`, { headers: getAuthHeaders() });
        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Unable to refresh overdue installments.');
        if (request !== overdueRequest.current) return;
        rows.push(...(Array.isArray(result.data) ? result.data : (Array.isArray(result) ? result : [])));
        lastPage = Math.max(1, Number(result.last_page) || 1);
        duePage += 1;
      } while (duePage <= lastPage);
      setOverdue(rows);
    } catch (e) {
      if (request === overdueRequest.current) { setOverdue([]); setOverdueError(e.message || 'Unable to refresh overdue installments.'); }
    }
  }, [companyId]);

  const refreshPayments = () => Promise.all([fetchList(), fetchOverdue()]);

  useEffect(() => { fetchList(); return () => { listRequest.current += 1; }; }, [fetchList]);
  useEffect(() => { fetchOverdue(); return () => { overdueRequest.current += 1; }; }, [fetchOverdue]);
  useEffect(() => { setPage(1); setRecording(false); }, [companyId]);

  useEffect(() => {
    let active = true;
    fetch(`${API}/companies/${companyId}/academy/enrollments?per_page=500&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] }).then(j => { if (active) setEnrollments(Array.isArray(j.data) ? j.data : []); }).catch(() => { if (active) setEnrollments([]); });
    return () => { active = false; };
  }, [companyId]);

  const reversePayment = async (id) => {
    const reason = window.prompt('Reversal reason:');
    if (reason === null) return;
    const res = await fetch(`${API}/companies/${companyId}/academy/payments/${id}/reverse`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Reversal failed'); return; }
    refreshPayments();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>💳 Student Payments</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={btnSecondary} onClick={() => setShowOverdue(v => !v)}>{showOverdue ? 'Hide' : 'Show'} Overdue ({overdue.length})</button>
          <button style={btnPrimary} onClick={() => setRecording(true)}>+ Record Payment</button>
        </div>
      </div>
      {error && <p role="alert">{error}</p>}
      {overdueError && <p role="alert">{overdueError}</p>}

      {showOverdue && (
        <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead><tr>{['Enrollment', 'Due Date', 'Amount', 'Amount Paid', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
              <tbody>
                {overdue.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No overdue installments</td></tr>}
                {overdue.map(o => (
                  <tr key={o.id}>
                    <td style={tdStyle}>{o.payment_plan?.enrollment?.student ? o.payment_plan.enrollment.student.full_name : '—'}</td>
                    <td style={tdStyle}>{o.due_date}</td>
                    <td style={tdStyle}>{fmtMoney(o.amount_due)}</td>
                    <td style={tdStyle}>{fmtMoney(o.amount_paid)}</td>
                    <td style={tdStyle}><Badge value={o.status} /></td>
                    <td style={tdStyle}><button style={{ ...btnSmall, color: '#b45309' }} onClick={() => waiveInstallment(o)}>Waive</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input style={{ ...inputStyle, width: 360 }} aria-label="Search payments" placeholder="Student name, code, phone or payment reference" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Receipt #', 'Student', 'Amount', 'Method', 'Date', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No payments</td></tr>}
              {list.map(p => (
                <tr key={p.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{p.payment_code || p.id}</td>
                  <td style={tdStyle}>{p.enrollment?.student ? p.enrollment.student.full_name : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{fmtMoney(p.amount)}</td>
                  <td style={tdStyle}>{paymentMethodLabel(p.payment_method)}</td>
                  <td style={tdStyle}>{p.payment_date}</td>
                  <td style={tdStyle}><Badge value={p.status} /></td>
                  <td style={tdStyle}>
                    {p.status !== 'reversed' && <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => reversePayment(p.id)}>Reverse</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div aria-label="Payment pagination" style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
        <button style={btnSecondary} disabled={loading || page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
        <span>Page {page} of {pagination.last_page} · {pagination.total} payments</span>
        <button style={btnSecondary} disabled={loading || page >= pagination.last_page} onClick={() => setPage(p => p + 1)}>Next</button>
      </div>

      {recording && <PaymentModal companyId={companyId} enrollments={enrollments} onClose={() => setRecording(false)} onSaved={() => { setRecording(false); refreshPayments(); }} />}
    </div>
  );
}

function PaymentModal({ companyId, enrollments, onClose, onSaved }) {
  const [form, setForm] = useState({
    enrollment_id: '', installment_id: '', amount: '', payment_method: 'cash',
    payment_date: new Date().toISOString().slice(0, 10), notes: '',
  });
  const [installments, setInstallments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form.enrollment_id) { setInstallments([]); return; }
    fetch(`${API}/companies/${companyId}/academy/enrollments/${form.enrollment_id}/payment-plan`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null).then(j => setInstallments(j?.installments || [])).catch(() => setInstallments([]));
  }, [companyId, form.enrollment_id]);

  const save = async () => {
    if (!form.enrollment_id || !form.amount) { alert('Enrollment and amount are required'); return; }
    setSaving(true);
    try {
      const body = { ...form, installment_id: form.installment_id || null };
      const res = await fetch(`${API}/companies/${companyId}/academy/payments`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Record Payment</h3>
        <FormRow label="Enrollment *">
          <select aria-label="Enrollment" style={inputStyle} value={form.enrollment_id} onChange={e => setForm({ ...form, enrollment_id: e.target.value, installment_id: '' })}>
            <option value="">Select enrollment</option>
            {enrollments.map(en => <option key={en.id} value={en.id}>{en.student ? en.student.full_name : en.enrollment_code} — {en.cohort?.name}</option>)}
          </select>
        </FormRow>
        {installments.length > 0 && (
          <FormRow label="Installment (optional)">
            <select style={inputStyle} value={form.installment_id} onChange={e => setForm({ ...form, installment_id: e.target.value })}>
              <option value="">None / single payment</option>
              {installments.map(i => <option key={i.id} value={i.id}>#{i.installment_number} — {fmtMoney(i.amount_due)} due {i.due_date} ({i.status})</option>)}
            </select>
          </FormRow>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormRow label="Amount *"><input aria-label="Amount" type="number" step="0.01" style={inputStyle} value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></FormRow>
          <FormRow label="Payment Date"><input type="date" style={inputStyle} value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} /></FormRow>
        </div>
        <FormRow label="Payment Method">
          <select style={inputStyle} value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
            {['cash', 'card', 'visa', 'bank_transfer', 'cheque', 'wish', 'omt', 'neo', 'other'].map(m => <option key={m} value={m}>{paymentMethodLabel(m)}</option>)}
          </select>
        </FormRow>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 60 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
